/* ================================================================ */
// MAP ACTIONS | index.js
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import axios from 'axios';
import { VisEngineTypes, LayerTypes } from '@myglobe/core/src/types';
import {getState as getMapState, getLayer} from '../../selectors/Data';
import {buildLayers} from '../../utils/vis-builder';
import {isNullOrUndefined} from 'util';
/* ========================================== */
// Actions
/* ========================================== */
// DeckGL
export const UPDATE_DECK_LAYER_CLASSES = 'UPDATE_DECK_LAYER_CLASSES';
export const UPDATE_DECK_LAYER_CLASSES_FAILURE = 'UPDATE_DECK_LAYER_CLASSES_FAILURE';
export const UPDATE_DECK_LAYER_CLASSES_SUCCESS = 'UPDATE_DECK_LAYER_CLASSES_SUCCESS';
// Base Config
export const UPDATE_MAP_CONFIG = 'UPDATE_MAP_CONFIG';
export const UPDATE_MAP_CONFIG_FAILURE = 'UPDATE_MAP_CONFIG_FAILURE';
export const UPDATE_MAP_CONFIG_SUCCESS = 'UPDATE_MAP_CONFIG_SUCCESS';
// ViewState
export const UPDATE_MAP_VIEW_STATE = 'UPDATE_MAP_VIEW_STATE';
// Style
export const UPDATE_MAP_STYLES = 'UPDATE_MAP_STYLES';
export const UPDATE_MAP_STYLES_FAILURE = 'UPDATE_MAP_STYLES_FAILURE';
export const UPDATE_MAP_STYLES_SUCCESS = 'UPDATE_MAP_STYLES_SUCCESS';
// Layer Classes
export const UPDATE_LAYER_CLASSES = 'UPDATE_LAYER_CLASSES';
// Layers
export const UPDATE_MAP_LAYERS = 'UPDATE_MAP_LAYERS';
export const UPDATE_MAP_LAYERS_FAILURE = 'UPDATE_MAP_LAYERS_FAILURE';
export const UPDATE_MAP_LAYERS_SUCCESS = 'UPDATE_MAP_LAYERS_SUCCESS';
// MapState (contains viewstate, style, layers)
export const UPDATE_MAP_STATE_FAILURE = 'UPDATE_MAP_STATE_FAILURE';

export const UPDATE_MAP_TILES_LOADED = 'UPDATE_MAP_TILES_LOADED';


export function updateDeckLayerClasses(layers) {
    return (dispatch) => {
        dispatch({type: UPDATE_DECK_LAYER_CLASSES});
        // Get set of missing imports
        new Promise((resolve,reject)=>{
            try {
                const layerClasses = Array.from(new Set(layers.map(layer=>{
                    const type = layer.type;
                    if(LayerTypes.deckgl.layers.includes(type))
                        return import('@deck.gl/aggregation-layers').then(module=>{return module});
                    if(LayerTypes.deckgl.aggregationLayers.includes(type))
                        return import('@deck.gl/aggregation-layers').then(module=>{return module});
                    if(LayerTypes.deckgl.geoLayers.includes(type))
                        return import('@deck.gl/geo-layers').then(module=>{return module});
                    if(LayerTypes.deckgl.custom.includes(type)) {
                        // Attempt fetch of custom layer
                        try {
                            return import(`@myglobe/core/dist/Map/layers/${type}`).then(module=>{return module.default});
                        } catch (e) {
                            console.log(e);
                        }
                    }
                    return null;
                }))).filter(layerClass=>!isNullOrUndefined(layerClass));
                resolve(layerClasses);
            } catch (error) {
                reject(error);
            }
        })
            .then(layerClasses=>dispatch({ type: UPDATE_DECK_LAYER_CLASSES_SUCCESS, payload: layerClasses }))
            .catch(error=>dispatch({type: UPDATE_DECK_LAYER_CLASSES_FAILURE, error, payload: 'deck layer classes import error'}));
    }
};
/* ========================================== */
/**
 * Update Map Config
 * -----------------
 * Updates the mapbox user id and
 * mapbox access token for style and mapbox api queries
 *
 * @param {object} globeConfig incoming globe config
 */
/* ========================================== */
export function updateMapConfig(globeConfig) {
    return (dispatch) => {
        dispatch({type: UPDATE_MAP_CONFIG});
        // Extract Map Config Updates
        const {mapboxUserId, mapboxToken} = globeConfig;
        if(mapboxToken && mapboxUserId) dispatch({type: UPDATE_MAP_CONFIG_SUCCESS, payload: {mapboxUserId, mapboxToken}});
        else dispatch({type: UPDATE_MAP_CONFIG_FAILURE, error: 'mapboxUserId or mapboxToken not provided'});
    };
}
/* ========================================== */
/**
 * Update Map View
 * ---------------
 * Updates the map's viewState with the new
 * viewState object
 *
 * @param {object} viewState map's view state
 */
/* ========================================== */
export function updateMapViewState(viewState) {
    return (dispatch) => {
        /*if(viewState.destination) {
            if(viewState.destination.x < -4700000) viewState.destination.x = -4700000;
            else if(viewState.destination.x > -5090000) viewState.destination.x = -5090000;
            if(viewState.destination.y > 3050000) viewState.destination.y = 3050000;
            else if(viewState.destination.y < 2900000) viewState.destination.y = 2900000;
        }*/
        dispatch({type: UPDATE_MAP_VIEW_STATE, payload: viewState});
    };
}
/* ========================================== */
/**
 * Update Map Style
 * ----------------
 * Updates the map's mapbox style
 *
 * @param {string} mapStyle map style string
 */
/* ========================================== */
/*export function updateMapStyle(mapStyle) {
    return (dispatch) => {
        dispatch({type: UPDATE_MAP_STYLE, payload: mapStyle});
    };
}*/
export function updateMapStyle(styles) {
    return (dispatch, getState) => {
        const {mapboxUserId, mapboxToken} = getState().Map;
        dispatch({type: UPDATE_MAP_STYLES});
        axios.get(`https://api.mapbox.com/styles/v1/${mapboxUserId}/${styles}?fresh=true&access_token=${mapboxToken}`).then( (result) => {
            console.debug(result);
            axios.get(`https://api.mapbox.com/styles/v1/${mapboxUserId}/${styles}/sprite?fresh=true&access_token=${mapboxToken}`).then((spriteJSON) => {
                dispatch({type: UPDATE_MAP_STYLES_SUCCESS, payload: { style: result.data, spriteJSON: spriteJSON.data, spriteImage: `https://api.mapbox.com/styles/v1/${mapboxUserId}/${styles}/sprite.png?fresh=true&access_token=${mapboxToken}` }});
            }).catch((error) => {
                dispatch({ type: UPDATE_MAP_STYLES_FAILURE, error, payload: 'spriteJSON error' });
            });
        }).catch((error) => {
            dispatch({ type: UPDATE_MAP_STYLES_FAILURE, error, payload: 'style error' });
        });
    };
}
/* ========================================== */
/**
 * Update Map Layers
 * -----------------
 * Updates the map's current vis layers
 *
 * @param {object} layers vis layers
 */
/* ========================================== */
export function updateMapLayers(layers) {
    return (dispatch) => {
        dispatch({type: UPDATE_MAP_LAYERS});
        console.debug('Map (Actions) | ', 'updateMapLayers -', 'layers:', layers);
        try {
            dispatch({type: UPDATE_MAP_LAYERS_SUCCESS, payload: {layers} });
        } catch (e) {
            dispatch({type: UPDATE_MAP_LAYERS_FAILURE, error: e, payload: 'buildLayers error'});
        }
    }
}
/*
// This update format does not function correctly in its current form
// TODO: revisit this form of layer update and see if vis builder is still required in any form
export function updateMapLayers(layers) {
    return (dispatch, getState) => {
        // Get pre-loaded layers from MapReducer
        dispatch({type: UPDATE_MAP_LAYERS});
        const store = getState();
        const layerConfigs = layers.map(layerId=>getLayer(store, layerId));
        const {deckLayers, mapboxLayers, markers} = getState().Map;
        //const newLoadedLayers = Array.from(new Set([...loadedLayers, ...layers]));
        //dispatch({type: UPDATE_MAP_LAYERS, payload: {mapboxLayers: layers, loadedLayers: newLoadedLayers}});

        var currentDeckLayers = [];
        var currentMapboxLayers = [];
        var currentMarkers = [];

        // TODO: figure out why the hell layers are just ids here without type context

        // Construct incoming layers
        const unloadedLayers = layerConfigs.filter(layer=>{
            console.debug('Map (Actions) | ', 'updateMapLayers (Build Layers) -', 'unloadedLayer:', layer);
            // Handle filter according to mapping engine

            // Search current deck layers for layer
            /*const deckLayer = deckLayers.find(dLayer=>dLayer.props.data[0].id === layer.id);
            if(deckLayer) {
                // Existing layer found, add to current deck layer array and filter out
                currentDeckLayers.push(deckLayer);
                return false;
            }
            const mapboxLayer = mapboxLayers.find(mLayer=>mLayer.id === layer.id);
            if(mapboxLayer) {
                // Existing layer found, add to current mapbox layer array and filter out
                currentMapboxLayers.push(mapboxLayer);
                return false;
            }
            // Marker
            const marker = markers.find(mark=>mark.id === layer.id);
            if(marker) {
                // Add marker to current markers
                currentMarkers.push(marker);
                return false;
            }
            return true;
            //return !markers.some(marker=>marker.id === layer.id);
            */

            /*
            switch(layer.type) {
            case VisEngineTypes.DECKGL: {
                // Search current deck layers for layer
                const deckLayer = deckLayers.find(dLayer=>dLayer.props.data[0].id === layer.id);
                if(deckLayer) {
                    // Existing layer found, add to current deck layer array and filter out
                    currentDeckLayers.push(deckLayer);
                    return false;
                } else {
                    return true;
                }
            }
            case VisEngineTypes.MAPBOX: {
                const mapboxLayer = mapboxLayers.find(mLayer=>mLayer.id === layer.id);
                if(mapboxLayer) {
                    // Existing layer found, add to current mapbox layer array and filter out
                    currentMapboxLayers.push(mapboxLayer);
                    return false;
                } else {
                    return true;
                }
            }
            default: {
                // Marker
                const marker = markers.find(mark=>mark.id === layer.id);
                if(marker) {
                    // Add marker to current markers
                    currentMarkers.push(marker);
                    return false;
                } else {
                    // No marker found for corresponding id (unloaded marker)
                    return true;
                }//return !markers.some(marker=>marker.id === layer.id);
            }
            }
        });

        try {
            // Check for unloaded layers
            if(unloadedLayers.length > 0) {
                // Build new layers
                buildLayers(unloadedLayers).then(newLayers=>{
                    console.debug('Map (Actions) | ', 'updateMapLayers (Build Layers) -', 'unloadedLayers:', unloadedLayers, 'newLayers:', newLayers);
                    /*if(newLayers.deckLayers) {
                        currentDeckLayers = [...currentDeckLayers, ...newLayers.deckLayers];
                    }
                    if(newLayers.mapboxLayers) {
                        currentMapboxLayers = [...currentMapboxLayers, ...newLayers.mapboxLayers];
                    }
                    if(newLayers.markers) {
                        currentMarkers = [...currentMarkers, ...newLayers.markers];
                    }*/
                    // Send new layers and current layer ids to reducer

                    /*
                    const payload = {layers, ...newLayers};
                    dispatch({type: UPDATE_MAP_LAYERS_SUCCESS, payload }); //deckLayers: currentDeckLayers, mapboxLayers: currentMapboxLayers, markers: currentMarkers}});
                });
            } else {
                // Send current layer ids to reducer
                dispatch({type: UPDATE_MAP_LAYERS_SUCCESS, payload: {layers} }); //deckLayers: currentDeckLayers, mapboxLayers: currentMapboxLayers, markers: currentMarkers}});
            }
        } catch (e) {
            dispatch({type: UPDATE_MAP_LAYERS_FAILURE, error: e, payload: 'buildLayers error'});
        }
    };
}
*/
/* ========================================== */
/**
 * Update Map State
 * ----------------
 * Updates the entire map's state according to
 * the keys given in the state argument via the
 * predefined update actions
 *
 * @param {object} state
 */
export function updateMapState(state) {
    return (dispatch, getState) => {
        if(state) {
            // Check Layers
            if(state.layers && state.layers.length > 0) {

                // Get layers from DataReducer
                const {layers} = getState().Data;

                // Filter out map layers specified by state layers (layerIDs) array
                // NOTE: this prevents null layer values from returning
                const currentLayers = layers.byId.filter(layer=>state.layers.some(element=>element === layer));//.map(layerID=>layers.byHash[layerID]);
                console.debug('Map (Actions) | ', 'updateMapState (handle layers) -', 'state layers:', state.layers, 'currentLayers:', currentLayers);
                // Update map layers via action
                dispatch(updateMapLayers(currentLayers));
            }
            if(!isNullOrUndefined(state.mapStyle)) {
                // TODO: Fix array vs single mapstyle query problem
                // Update map style via action
                const {mapStyle} = getState().Map;
                console.debug('Map (Actions) | ', 'updateMapState (handle mapStyle) -', 'currentMapStyle:', mapStyle, 'nextMapStyle:', state.mapStyle);
                const mapStyleId = typeof mapstyle === 'string' ? mapStyle : mapStyle?.id;
                if(state.mapStyle[0] !== mapStyleId)
                    dispatch(updateMapStyle(state.mapStyle));
            }
            if(state.viewState) {
                // Update map view state via action
                dispatch(updateMapViewState(state.viewState));
                //dispatch({type: UPDATE_VIEW_STATE, payload: state.viewState});
            }
        } else {
            dispatch({type: UPDATE_MAP_STATE_FAILURE, error: new Error('null state'), payload: 'null state error'});
        }
    };
}
/* ========================================== */
/**
 * Update Map State By Id
 * ----------------------
 * Retrieves map state by id via selector
 * and handles map state updating through existing
 * updateMapState action according to the state's keys
 *
 * @param {string} stateId state name identifier
 */
export function updateMapStateById(stateId) {
    return (dispatch) => {
        const state = getMapState(stateId);
        dispatch(updateMapState(state));
    }
}